import { useEffect, useMemo, useState } from 'react';
import { Award, CheckCircle2, Clock, Coffee, Flag, MessageSquare, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { AchievementGrid, LevelBadge } from './WorkAchievements';
import { computeAchievements } from '@/features/workMode/gamification';
import { formatDuration } from '@/features/workMode/api';
import type { WorkProfileStats, WorkShiftSummary as Summary } from '@/features/workMode/types';

interface WorkShiftSummaryProps {
  name: string;
  summary: Summary;
  statsBefore: WorkProfileStats | undefined;
  statsAfter: WorkProfileStats | undefined;
  onDone: () => void;
  /** Auto-return to the lock screen after this many seconds. */
  autoCloseSeconds?: number;
}

/** The sign-out screen: what you did, what you earned, back to the lock screen. */
export function WorkShiftSummary({ name, summary, statsBefore, statsAfter, onDone, autoCloseSeconds = 45 }: WorkShiftSummaryProps) {
  const [left, setLeft] = useState(autoCloseSeconds);
  useEffect(() => {
    const id = window.setInterval(() => setLeft((n) => n - 1), 1000);
    return () => window.clearInterval(id);
  }, []);
  useEffect(() => {
    if (left <= 0) onDone();
  }, [left, onDone]);

  const newBadges = useMemo(() => {
    if (!statsAfter) return [];
    const after = computeAchievements(statsAfter).filter((a) => a.earned);
    if (!statsBefore) return after;
    const before = new Set(computeAchievements(statsBefore).filter((a) => a.earned).map((a) => a.id));
    return after.filter((a) => !before.has(a.id));
  }, [statsBefore, statsAfter]);

  const first = name.split(' ')[0] || name;

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 px-6 py-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-wide text-muted-foreground">Shift complete</p>
          <h1 className="text-3xl font-medium tracking-headline text-foreground">Nice work, {first}.</h1>
        </div>
        <Button size="lg" onClick={onDone} className="shrink-0">
          Done{left > 0 ? ` (${left})` : ''}
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Stat icon={CheckCircle2} label="Units built" value={String(summary.units)} tone="good" />
        <Stat icon={Clock} label="Avg per unit" value={formatDuration(summary.avg_seconds)} />
        <Stat icon={Award} label="Best unit" value={formatDuration(summary.best_seconds)} />
        <Stat icon={Clock} label="Time working" value={formatDuration(summary.worked_seconds)} />
        <Stat icon={Coffee} label="On break" value={formatDuration(summary.break_seconds)} />
        <Stat icon={Trash2} label="Scrapped" value={String(summary.scrapped)} tone={summary.scrapped ? 'warn' : undefined} />
      </div>

      <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <Flag className="h-4 w-4" /> {summary.defects} defect{summary.defects === 1 ? '' : 's'} reported
        </span>
        <span className="inline-flex items-center gap-1.5">
          <MessageSquare className="h-4 w-4" /> {summary.notes} note{summary.notes === 1 ? '' : 's'} left for management
        </span>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_1.2fr]">
        <section className="rounded-2xl border border-border bg-card p-5">
          <h2 className="mb-4 text-sm uppercase tracking-wide text-muted-foreground">Your level</h2>
          {statsAfter ? <LevelBadge stats={statsAfter} size="lg" /> : <Skeleton className="h-24 w-full" />}
          {newBadges.length > 0 && (
            <div className="mt-5 space-y-2">
              <p className="text-sm font-medium text-bright-moss-dark">New badge{newBadges.length > 1 ? 's' : ''} earned today</p>
              <ul className="space-y-1.5">
                {newBadges.map((b) => (
                  <li key={b.id} className="flex items-center gap-2 rounded-lg border border-bright-moss bg-bright-moss/20 px-3 py-2 text-sm">
                    <Award className="h-4 w-4 text-bright-moss-dark" />
                    <span className="font-medium text-foreground">{b.title}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
        <section className="rounded-2xl border border-border bg-card p-5">
          <h2 className="mb-4 text-sm uppercase tracking-wide text-muted-foreground">Badges</h2>
          {statsAfter ? <AchievementGrid stats={statsAfter} compact /> : <Skeleton className="h-24 w-full" />}
        </section>
      </div>
    </div>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: typeof Clock;
  label: string;
  value: string;
  tone?: 'good' | 'warn';
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <p className={`mt-1 text-2xl font-medium ${tone === 'good' ? 'text-bright-moss-dark' : tone === 'warn' ? 'text-fresh-salmon-dark' : 'text-foreground'}`}>{value}</p>
    </div>
  );
}
